import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { GraduationCapIcon, TrendingUpIcon, UsersIcon, MonitorIcon } from 'lucide-react';
import { DEFAULT_HIGHLIGHTS, EDUCATION_CITATIONS, getDefaultExploreBridge, getExploreBridge } from '../data/exploreBridges';
import { useMetaTags } from '../hooks/useMetaTags';
import { buildClicksSearchUrl } from '../utils/clicksSearchUrl';
import { BrandedExploreBridge } from '../components/BrandedExploreBridge';
import { getUniversityBySlug } from '../data/universityData';

// Ad-copy bridge template served at /explore/{brand}-{version}
// Icons paired positionally with the outcome stats: earnings, graduates,
// online enrollment.
const HIGHLIGHT_ICONS = [TrendingUpIcon, UsersIcon, MonitorIcon];
// (e.g. /explore/UAGC-v1). Layout is fixed across every template:
// site nav → hero call to action → short paragraph section → footer.
// The copy, imagery, and outbound destination come from data/exploreBridges.
export function ExploreBridgePage() {
  const {
    bridgeSlug
  } = useParams<{
    bridgeSlug: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Fall back to the path segment so statically-registered routes (which
  // have no :bridgeSlug param) resolve the same as dynamic ones.
  const slug = bridgeSlug || location.pathname.replace(/\/+$/, '').split('/').filter(Boolean).pop();
  // Previewing the dynamic route directly (path shown as "/explore/:bridgeSlug")
  // has no real slug, so show the first template rather than a dead end.
  const isPlaceholderSlug = !slug || slug.startsWith(':');
  const bridge = isPlaceholderSlug ? getDefaultExploreBridge() : getExploreBridge(slug);
  useMetaTags({
    title: bridge?.metaTitle || 'Explore Online Degrees | FindOnlineDegrees',
    description: bridge?.metaDescription || 'Explore accredited online degree programs from our partner schools.',
    ogImage: bridge?.heroImage,
    // Ad-copy pages shouldn't compete with organic pages in search.
    robots: 'noindex, follow'
  });
  const handleNavigate = (page: string) => {
    navigate(page === 'compare' ? '/' : `/${page}`);
    window.scrollTo(0, 0);
  };
  // Carry any lead data and tracking params on this page's URL through to
  // the destination so the click can be attributed and the user isn't
  // asked for information they already provided.
  const handleCta = () => {
    if (!bridge) return;
    if (bridge.clicksSearch) {
      const url = buildClicksSearchUrl(location.search, bridge.clicksSearch);
      console.log('[Explore Bridge] redirecting to:', url);
      window.location.href = url;
      return;
    }
    if (!bridge.destinationUrl) return;
    const search = location.search.replace(/^\?/, '');
    const isExternal = /^https?:\/\//i.test(bridge.destinationUrl);
    const separator = bridge.destinationUrl.includes('?') ? '&' : '?';
    const destination = search ? `${bridge.destinationUrl}${separator}${search}` : bridge.destinationUrl;
    if (isExternal) {
      window.location.href = destination;
      return;
    }
    navigate(destination);
    window.scrollTo(0, 0);
  };
  // Branded ad copy renders the advertiser-styled layout instead.
  const university = bridge?.universitySlug ? getUniversityBySlug(bridge.universitySlug) : undefined;
  if (bridge && bridge.layout === 'branded' && university) {
    return <BrandedExploreBridge bridge={bridge} university={university} onCta={handleCta} data-id="element-1568" data-mp="3lw8ctuvdblvh" />;
  }
  if (!bridge) {
    return <div className="min-h-screen bg-white flex flex-col" data-id="element-1569" data-mp="3gzrlyswsatep">
        <Header currentPage="explore" onNavigate={handleNavigate} data-id="element-1570" data-mp="3bxk79vkkkz8o" />
        <main className="flex-grow flex items-center justify-center px-4 py-24" data-id="element-1571" data-mp="25i9tipknlg7h">
          <div className="text-center max-w-md" data-id="element-1572" data-mp="20jexun1l7x5y">
            <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1573" data-mp="20ind679j3jug">
              This page isn't available
            </h1>
            <p className="text-gray-600 mb-8" data-id="element-1574" data-mp="3pcb7ul9w96wn">
              The link you followed may be expired or mistyped. Explore our
              accredited online programs instead.
            </p>
            <button onClick={() => handleNavigate('compare')} className="px-6 py-3 bg-[#e8594f] text-white font-bold rounded-lg hover:bg-[#d54a41] transition-colors" data-id="element-1575" data-mp="3fgzrjyiu7rms">
              Browse Online Degrees
            </button>
          </div>
        </main>
        <Footer onNavigate={handleNavigate} data-id="element-1576" data-mp="gxanyekt54k0" />
      </div>;
  }
  const highlights = bridge.highlights ?? DEFAULT_HIGHLIGHTS;
  return <div className="min-h-screen bg-white flex flex-col" data-id="element-1577" data-mp="22wgnbalvjl6">
      <Header currentPage="explore" onNavigate={handleNavigate} data-id="element-1578" data-mp="6ybsxysyt7fh" />
      <main className="flex-grow" data-id="element-1579" data-mp="2mq9bl5lzqky6">
        {/* Hero + call to action */}
        <section className="relative border-b-4 border-[#e8594f]" data-id="element-1580" data-mp="2rpubbi8zixby">
          <img src={bridge.heroImage} alt={bridge.heroImageAlt} className="absolute inset-0 w-full h-full object-cover" data-id="element-1581" data-mp="2rr5f3i2fk532" />
          <div className="absolute inset-0 bg-gray-900/40 lg:bg-gray-900/20" aria-hidden="true" data-id="element-1582" data-mp="14txlwzl032pw" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-id="element-1583" data-mp="1is48vkoevias">
            <div className="lg:grid lg:grid-cols-2 lg:gap-12" data-id="element-1584" data-mp="1ertr7ar9b3g3">
              <div className="hidden lg:block" aria-hidden="true" data-id="element-1585" data-mp="r0y4ut5kl0g8" />
              <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10 text-center max-w-xl mx-auto lg:mx-0" data-id="element-1586" data-mp="qzddfcejxxml">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight" data-id="element-1587" data-mp="ztt2b0osprvg">
                  {bridge.cardHeadline}
                </h1>
                <p className="mt-4 text-lg font-semibold text-[#e8594f]" data-id="element-1588" data-mp="v1rpndnptz4w">
                  {bridge.cardSubhead}
                </p>
                <button onClick={handleCta} className="mt-8 w-full sm:w-auto px-10 py-4 bg-[#e8594f] text-white text-lg font-bold rounded-lg shadow-md hover:bg-[#d54a41] hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8594f]" data-id="element-1589" data-mp="3c55sntniche">
                  {bridge.ctaLabel}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* National education outcomes — compact band directly under the hero */}
        <section className="bg-gray-50 border-b border-gray-200" aria-label="Education outcomes" data-id="element-1590" data-mp="1jj41glqqkrrv">
          <dl className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid gap-6 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-gray-200" data-id="element-1591" data-mp="1ohdx0xgdhue0">
            {highlights.map((item, i) => {
            const Icon = HIGHLIGHT_ICONS[i % HIGHLIGHT_ICONS.length];
            return <div key={item.label} className="flex gap-4 sm:px-6 sm:first:pl-0 sm:last:pr-0" data-id="element-1592" data-mp="34um0kvdsri9x" data-mpi={i}>
                  <Icon className="mt-1 h-5 w-5 flex-shrink-0 text-[#e8594f]" aria-hidden="true" data-id="element-1593" data-mp="y753qhf8bc2j" />
                  <div data-id="element-1594" data-mp="t8k34gw2eqdk">
                    <dt className="text-xl font-extrabold text-gray-900 tracking-tight leading-none" data-id="element-1595" data-mp="taeep4lg564z">
                      {item.stat}{' '}
                      <span className="text-sm font-semibold text-gray-500" data-id="element-1596" data-mp="jj0lfek392ka">
                        {item.label}
                      </span>
                      {item.note && <sup className="ml-0.5 text-[10px] font-semibold text-gray-400" data-id="element-1597" data-mp="ahslyt5u5vom">
                          [{item.note}]
                        </sup>}
                    </dt>
                    <dd className="mt-2 text-sm text-gray-600 leading-relaxed" data-id="element-1598" data-mp="jwce5sjr8sm">
                      {item.body}
                    </dd>
                  </div>
                </div>;
          })}
          </dl>
        </section>

        {/* Short paragraph section */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-id="element-1599" data-mp="13s1tnnzwf0q2">
          <div className="lg:grid lg:grid-cols-[1fr_360px] lg:gap-12 lg:items-start" data-id="element-1600" data-mp="13ue7boymioxb">
            <div data-id="element-1601" data-mp="18t7k1f636bwf">
              <span className="mx-auto lg:mx-0 mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-[#e8594f]/10" aria-hidden="true" data-id="element-1602" data-mp="u28g4hg74bj6">
                <GraduationCapIcon className="h-6 w-6 text-[#e8594f]" data-id="element-1603" data-mp="yyyvt3o39542" />
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center lg:text-left" data-id="element-1604" data-mp="l3n1uyhrbio7">
                {bridge.sectionHeading}
              </h2>
              <div className="mt-8 space-y-5" data-id="element-1605" data-mp="b4fl01btd5wc">
                {bridge.paragraphs.map(paragraph => <p key={paragraph.slice(0, 40)} className="text-lg text-gray-600 leading-relaxed" data-id="element-1606" data-mp="g38snd4f62ol">
                    {paragraph}
                  </p>)}
              </div>
            </div>

            <img src="https://cdn.magicpatterns.com/patterns/generated-images/c770ed7b-e4d2-4980-b486-3ba0bc2b040d.jpg" alt="Graduate in cap and gown holding her young daughter after her commencement ceremony" className="mt-10 lg:mt-0 w-full max-w-sm mx-auto lg:max-w-none rounded-2xl object-cover aspect-[4/5]" loading="lazy" data-id="element-1607" data-mp="3uxzpgrl1i956" />
          </div>

          <div className="mt-14 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between rounded-2xl bg-gray-50 border border-gray-200 p-6 sm:p-8" data-id="element-1608" data-mp="hiuwvh1aegcv">
            <p className="text-lg font-semibold text-gray-900 max-w-md" data-id="element-1609" data-mp="rhk7coqusyj3">
              {bridge.closingLine || bridge.cardSubhead}
            </p>
            <button onClick={handleCta} className="w-full sm:w-auto flex-shrink-0 px-10 py-4 bg-[#e8594f] text-white text-lg font-bold rounded-lg shadow-md hover:bg-[#d54a41] hover:shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8594f]" data-id="element-1610" data-mp="mk0qri6oo9nz">
              {bridge.ctaLabel}
            </button>
          </div>

          {bridge.disclosure && <p className="mt-10 text-xs text-gray-400 leading-relaxed" data-id="element-1611" data-mp="199a36dx40qu1">
              {bridge.disclosure}
            </p>}
        </section>

        {/* Citations for the national statistics above */}
        <section className="border-t border-gray-200 bg-white" aria-label="Citations" data-id="element-1612" data-mp="1wws1yteskp6i">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-1613" data-mp="1rxyjsnok8a00">
            <h2 className="text-sm font-bold text-gray-600 mb-3" data-id="element-1614" data-mp="1s01cjfh3kh8f">Citations</h2>
            <ol className="space-y-1.5" data-id="element-1615" data-mp="1n55k4e7y7rus">
              {EDUCATION_CITATIONS.links.map((href, i) => <li key={href} className="text-xs text-gray-500" data-id="element-1616" data-mp="1j54vk0r4y0a0" data-mpi={i}>
                  <span className="mr-1 text-gray-400" data-id="element-1617" data-mp="2fob1gq9lxl3s">[{i + 1}]</span>
                  <a href={href} target="_blank" rel="noopener noreferrer nofollow" className="underline break-all hover:text-gray-700" data-id="element-1618" data-mp="2awszbyvuleh8">
                    {href}
                  </a>
                </li>)}
            </ol>
            <div className="mt-5 space-y-1" data-id="element-1619" data-mp="6dtn7kof6q3u">
              {EDUCATION_CITATIONS.notes.map(note => <p key={note} className="text-xs text-gray-500 leading-relaxed" data-id="element-1620" data-mp="bf97hairwsl6">
                  {note}
                </p>)}
            </div>
          </div>
        </section>
      </main>
      <Footer onNavigate={handleNavigate} data-id="element-1621" data-mp="laaw8594zsn8" />
    </div>;
}